import { useMemo } from 'react'
import { KpiTile } from '../../charts/KpiTile'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { buildDrillRsql } from '../../drill'
import { runExport, type ExportColumn } from '../../../../lib/export'
import { buildStatusCountRows, type StatusCountRow } from './analyticsMappers'
import type { StatusCountBucket } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.filingsByStatus

const EXPORT_COLUMNS: ExportColumn<StatusCountRow>[] = [
  { id: 'label', header: 'Status', toText: (r) => r.label },
  { id: 'count', header: 'Count', toText: (r) => String(r.count) },
  { id: 'pct', header: '% of total', toText: (r) => `${r.pct.toFixed(2)}%` },
]

/**
 * AC1.2 — "Count by status": one KPI stat per known `ProgramFilingState` value (`filings-by-status`
 * is already zero-filled server-side, AC1 of the base tile) with a numeric count and a %-of-total
 * sub-label, 2 decimal places. No new backend read — a second view over the same widget key
 * `FilingsByStatusWidget` already fetches (same posture as `CreditsByJurisdictionStatusWidget`
 * being a second view over `credit-value-by-jurisdiction-fy`).
 */
export function StatusCountRowWidget({ filter, skipRefetch, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<StatusCountBucket[]>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data) ? data : []), [data])
  const rows = useMemo(() => buildStatusCountRows(buckets), [buckets])
  const total = rows.reduce((sum, r) => sum + r.count, 0)

  const frameState = status === 'ok' && rows.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Count by status"
      icon="briefcase"
      state={frameState}
      message={message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={rows.length > 0 ? { onClick: () => runExport('csv', 'Count by status', rows, EXPORT_COLUMNS) } : undefined}
    >
      <KpiTile
        title="Count by status"
        value={String(total)}
        valueCaption="filings across every known status"
        breakdown={rows.map((r) => ({ key: r.status, label: r.label, value: `${r.count} (${r.pct.toFixed(2)}%)` }))}
      />
    </WidgetFrame>
  )
}
